import { useState } from 'react';
import Input from '../ui/Input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface RecipientsFieldProps {
  value: string[];
  onChange: (next: string[]) => void;
}

/**
 * Extra per-site alert recipients — kyle@/katie@ are always notified, these
 * are additional people alerted only when THIS site goes down. Emails are
 * entered one at a time (Enter or comma) and shown as removable chips.
 */
export default function RecipientsField({ value, onChange }: RecipientsFieldProps) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const email = draft.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_RE.test(email)) {
      setError('Enter a valid email address');
      return;
    }
    if (value.includes(email)) {
      setError('Already added');
      setDraft('');
      return;
    }
    onChange([...value, email]);
    setDraft('');
    setError(null);
  };

  const remove = (email: string) => onChange(value.filter((e) => e !== email));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <span className="t-eyebrow">Alert Recipients</span>

      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {value.map((email) => (
            <span
              key={email}
              className="t-num"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '7px',
                padding: '4px 6px 4px 10px',
                borderRadius: 'var(--radius-pill)',
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-hairline)',
                fontSize: 'var(--text-xs)',
                color: 'var(--text-body)',
              }}
            >
              {email}
              <button
                type="button"
                className="pressable"
                aria-label={`Remove ${email}`}
                onClick={() => remove(email)}
                style={{
                  display: 'inline-flex',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-faint)',
                  cursor: 'pointer',
                  padding: '2px',
                }}
              >
                <FontAwesomeIcon icon={faXmark} style={{ fontSize: '11px' }} />
              </button>
            </span>
          ))}
        </div>
      )}

      <Input
        icon="mail"
        placeholder="Add email, press Enter"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
      />

      {error ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-down)' }}>{error}</span>
      ) : (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
          Kyle and Katie are always notified. Add emails here to also alert others when this site goes down.
        </span>
      )}
    </div>
  );
}
